import { useMemo } from 'react';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { getCurrencyValue } from 'utilities';

export interface SelectionSummary {
  sum: number;
  count: number;
  numericCount: number;
  average: number;
  min: number;
  max: number;
}

interface SelectionBounds {
  start: { i: number; j: number };
  end: { i: number; j: number };
}

function hasSelectionBounds(cell: unknown): cell is SelectionBounds {
  if (!cell || typeof cell !== 'object') {
    return false;
  }
  const obj = cell as Record<string, unknown>;
  return (
    obj.start !== null &&
    obj.start !== undefined &&
    typeof obj.start === 'object' &&
    obj.end !== null &&
    obj.end !== undefined &&
    typeof obj.end === 'object'
  );
}

function extractNumericValue(cell: ICell | undefined): number | null {
  if (!cell || cell.hidden || cell.isTitleOrHeader || cell.isLegend) {
    return null;
  }

  const raw = cell.value;

  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return raw;
  }

  if (typeof raw === 'string') {
    const cleaned = raw.replace(/[,%$()]/g, '').trim();
    if (cleaned === '' || cleaned === '-') {
      return null;
    }
    const parsed = Number(cleaned);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
}

const useSelectionSummary = (
  selectedCell: ICell | undefined,
  data: ICell[][] | undefined | null,
  exchangeRate?: number,
  units?: string,
  useCellCurrencyFormat = false
): SelectionSummary | null => {
  const bounds = hasSelectionBounds(selectedCell) ? selectedCell : null;

  return useMemo(() => {
    if (!bounds || !data) {
      return null;
    }

    const { start, end } = bounds;

    const isSingleCell = start.i === end.i && start.j === end.j;
    if (isSingleCell) {
      return null;
    }

    const minRow = Math.min(start.i, end.i);
    const maxRow = Math.max(start.i, end.i);
    const minCol = Math.min(start.j, end.j);
    const maxCol = Math.max(start.j, end.j);

    let sum = 0;
    let count = 0;
    let numericCount = 0;
    let min = Infinity;
    let max = -Infinity;

    for (let row = minRow; row <= maxRow; row++) {
      const rowData = data[row];
      if (rowData) {
        for (let col = minCol; col <= maxCol; col++) {
          const cell = rowData[col];
          if (cell) {
            count++;
            const numVal = extractNumericValue(cell);
            if (numVal !== null) {
              const customCurrencyFormat = (
                cell.customFormat as { currency?: { exchangeRate?: number; units?: string } } | null | undefined
              )?.currency;
              const cellExchangeRate = customCurrencyFormat?.exchangeRate ?? exchangeRate;
              const cellUnits = customCurrencyFormat?.units ?? units;
              // Opt-in consumers honor per-cell currency formatting. The default preserves legacy
              // behavior by applying sheet units only when a non-identity FX rate is active.
              const convertedVal =
                cell.format?.style === 'currency' && (useCellCurrencyFormat || (exchangeRate && exchangeRate !== 1))
                  ? getCurrencyValue({
                    value: numVal,
                    exchangeRate: useCellCurrencyFormat ? cellExchangeRate : exchangeRate,
                    units: useCellCurrencyFormat ? cellUnits : units,
                  })
                  : numVal;
              numericCount++;
              sum += convertedVal;
              if (convertedVal < min) {
                min = convertedVal;
              }
              if (convertedVal > max) {
                max = convertedVal;
              }
            }
          }
        }
      }
    }

    if (numericCount === 0) {
      return null;
    }

    return {
      sum,
      count,
      numericCount,
      average: sum / numericCount,
      min,
      max,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bounds?.start.i,
    bounds?.start.j,
    bounds?.end.i,
    bounds?.end.j,
    data,
    exchangeRate,
    units,
    useCellCurrencyFormat,
  ]);
};

export default useSelectionSummary;
